<template>
  <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
    <div class="bg-white rounded-xl shadow-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <h2 class="text-xl font-semibold mb-6">{{ mode === 'create' ? 'New OAuth Application' : 'Edit Application' }}</h2>

      <form @submit.prevent="submit">
        <div class="mb-4">
          <label for="oauth-name" class="block text-sm font-medium text-gray-700 mb-1">Portal Name <span class="text-red-500">*</span></label>
          <input
            id="oauth-name"
            v-model="form.name"
            type="text"
            required
            class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div class="mb-4">
          <label for="oauth-redirect-uri" class="block text-sm font-medium text-gray-700 mb-1">
            Login Redirect URIs <span class="text-red-500">*</span>
            <span class="text-gray-400 font-normal text-xs ml-1">(one per line)</span>
          </label>
          <textarea
            id="oauth-redirect-uri"
            v-model="form.redirect_uri_raw"
            rows="4"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            :class="redirectUriError ? 'border-red-500' : ''"
            placeholder="https://app.example.com/callback"
            @input="redirectUriError = null"
          />
          <p v-if="redirectUriError" class="text-red-500 text-xs mt-1">{{ redirectUriError }}</p>
        </div>

        <div class="mb-4">
          <label for="oauth-logout-uri" class="block text-sm font-medium text-gray-700 mb-1">
            Logout Redirect URIs
            <span class="text-gray-400 font-normal text-xs ml-1">(one per line, optional)</span>
          </label>
          <textarea
            id="oauth-logout-uri"
            v-model="form.logout_redirect_uri_raw"
            rows="3"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://app.example.com/logout"
          />
        </div>

        <div class="mb-4">
          <label for="oauth-bypass" class="block text-sm font-medium text-gray-700 mb-1">
            Bypass Redirect URI Patterns
            <span class="text-gray-400 font-normal text-xs ml-1">(one per line, e.g. *.example.com)</span>
          </label>
          <textarea
            id="oauth-bypass"
            v-model="form.bypass_raw"
            rows="3"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            :class="bypassError ? 'border-red-500' : ''"
            placeholder="*.example.com"
            @input="validateBypass"
          />
          <p v-if="bypassError" class="text-red-500 text-xs mt-1">{{ bypassError }}</p>
          <p v-else class="text-gray-400 text-xs mt-1">Matches one subdomain level. *.com or * not allowed.</p>
        </div>

        <div v-if="mode === 'edit'" class="mb-4">
          <label for="oauth-remark" class="block text-sm font-medium text-gray-700 mb-1">
            Remark
            <span class="text-gray-400 font-normal text-xs ml-1">(please fill in reason for changes)</span>
          </label>
          <textarea
            id="oauth-remark"
            v-model="form.remark"
            rows="2"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p v-if="serverError" class="text-red-500 text-sm mb-4">{{ serverError }}</p>

        <div class="flex gap-3 justify-end">
          <button
            type="button"
            class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="!!bypassError || submitting"
            class="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {{ submitting ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const BLOCKED_TLDS = new Set(['com', 'io', 'net', 'org', 'co', 'gov', 'edu', 'info', 'biz']);

function validatePattern(p) {
  p = p.trim();
  if (p === '*') return "Wildcard-only '*' is not allowed";
  if (!p.startsWith('*.')) return `Pattern must start with '*.': ${p}`;
  const suffix = p.slice(2);
  const parts = suffix.split('.');
  if (parts.length < 2) return `Pattern must have at least one subdomain level: ${p}`;
  if (parts.length === 2 && BLOCKED_TLDS.has(parts[0].toLowerCase())) return `TLD-only wildcard not allowed: ${p}`;
  if (!/^\*\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/.test(p)) return `Invalid pattern format: ${p}`;
  return null;
}

export default {
  name: 'OAuthApplicationForm',
  emits: ['close', 'saved'],
  props: {
    mode: { type: String, default: 'create' },
    initialData: { type: Object, default: () => ({}) },
  },
  data() {
    const d = this.initialData;
    return {
      form: {
        name: d.name || '',
        redirect_uri_raw: (d.redirect_uri || []).join('\n'),
        logout_redirect_uri_raw: (d.logout_redirect_uri || []).join('\n'),
        bypass_raw: (d.redirect_uri_bypass_patterns || []).join('\n'),
        remark: d.remark || '',
      },
      bypassError: null,
      redirectUriError: null,
      serverError: null,
      submitting: false,
    };
  },
  methods: {
    validateBypass() {
      const patterns = this.form.bypass_raw.split('\n').map(p => p.trim()).filter(Boolean);
      for (const p of patterns) {
        const err = validatePattern(p);
        if (err) { this.bypassError = err; return; }
      }
      this.bypassError = null;
    },
    parseLines(raw) {
      return raw.split('\n').map(l => l.trim()).filter(Boolean);
    },
    async submit() {
      this.validateBypass();
      if (this.bypassError) return;

      const redirectUris = this.parseLines(this.form.redirect_uri_raw);
      if (redirectUris.length === 0) {
        this.redirectUriError = 'At least one Login Redirect URI is required';
        return;
      }
      this.redirectUriError = null;

      this.submitting = true;
      this.serverError = null;

      const trimmedName = this.form.name.trim();
      if (!trimmedName) {
        this.serverError = 'Application name is required';
        this.submitting = false;
        return;
      }

      const payload = {
        name: trimmedName,
        redirect_uri: redirectUris,
        logout_redirect_uri: this.parseLines(this.form.logout_redirect_uri_raw),
        redirect_uri_bypass_patterns: this.parseLines(this.form.bypass_raw),
      };
      if (this.mode === 'edit') payload.remark = this.form.remark;

      try {
        const url = this.mode === 'create'
          ? '/api/oauth-management/applications'
          : `/api/oauth-management/applications/${this.initialData.id}`;
        const method = this.mode === 'create' ? 'post' : 'put';
        const { data } = await axios[method](url, payload);
        this.$emit('saved', data);
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.serverError = detail.map(d => d.msg || JSON.stringify(d)).join(' / ');
        } else {
          this.serverError = detail || 'Save failed. Please try again.';
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
